import '@/assets/styles.scss';
import '@/assets/tailwind.css';
import Vue3Tour from 'vue3-tour';
import 'vue3-tour/dist/vue3-tour.css';
import { useLayout } from '@/layout/composables/layout';
import ConfirmationPopup from '@/layout/composables/popup';
// i18n localization files
import en from '@/locales/en.json';
import it from '@/locales/it.json';
import Aura from '@primevue/themes/aura';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import Panel from 'primevue/panel';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import ToastService from 'primevue/toastservice';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';
import App from './App.vue';
// Module-specific localization files
import { languages } from './modules/agricobots/index.ts';
import { TimeSync } from './utils/timesync/timesync';

import router from './router';
import { useAuthStore } from './stores/auth';

const pinia = createPinia();
const { initializeTheme } = useLayout();

// Get the initial locale from localStorage or default to 'it'
const getInitialLocale = () => {
  const savedLocale = localStorage.getItem('locale');
  return savedLocale || 'it';
};

// Initialize theme before creating the app
initializeTheme();

const i18n = createI18n({
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  messages: {
    it: { ...it, ...languages.it },
    en: { ...en, ...languages.en },
  },
  globalInjection: true,
});

const app = createApp(App);

app.use(pinia);
app.use(router);
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkTheme: true,
      darkModeSelector: '.app-dark',
      fontSize: '16px',
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);
app.use(i18n);
app.use(Vue3Tour);

// Register components globally
app.component('Panel', Panel);
app.component('Splitter', Splitter);
app.component('SplitterPanel', SplitterPanel);
app.component('ConfirmationPopup', ConfirmationPopup);

// Initialize auth state
const authStore = useAuthStore();
authStore.initAuth();

// Initialize time sync
TimeSync.initialize();

app.mount('#app');

/**
 * Dev-only asset simulator, exposed on the console as `simulaAsset()`.
 *
 * Vite replaces `import.meta.env.DEV` with a literal, so in a production build
 * this branch is dead code and Rollup never emits the module behind the dynamic
 * import. It must stay a dynamic import inside this guard for that to hold.
 */
if (import.meta.env.DEV) {
  import('./dev/assetSimulator').then(({ registerAssetSimulator }) => {
    registerAssetSimulator();
  });
}
